import { useCallback, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  getEdgeFadeStyle,
  useScrollEdges,
} from "@volca/ui/hooks/use-scroll-edges";
import { cn } from "@volca/ui/lib/utils";
import { useReviewQueueFilters } from "@/accounts-payable/ap-url-state";
import {
  InvoiceGroupCardSkeleton,
  LoadMoreButton,
  OwnerAndActionFilters,
  useResolveWithBasisCheck,
  useReviewQueueNavigation,
} from "@/accounts-payable/common";
import { InvoiceGroupCard } from "./invoice-group-card";
import { ReviewQueueEmpty } from "./review-queue-empty";
import { VendorFilter } from "./vendor-filter";

export const ApCombinedInvoicesTab = ({
  businessId,
  bulkApprovingIds,
  onBulkApprove,
  onBrowseInvoices,
}: {
  businessId: string | null;
  bulkApprovingIds: ReadonlySet<string>;
  onBulkApprove: (ids: string[]) => void;
  /** Sends an empty queue somewhere useful instead of naming a tab in prose. */
  onBrowseInvoices?: () => void;
}) => {
  const { setNode, edges } = useScrollEdges(true);
  const filters = useReviewQueueFilters();
  const { filtered } = filters;
  const {
    queue,
    items: queueItems,
    viewportRef,
    onViewportScroll,
    awaitingPosition,
    openInvoice,
    restoring,
  } = useReviewQueueNavigation(businessId, filters.apiFilters);
  const {
    isPending,
    isError,
    isFetching,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = queue;
  const {
    resolve: onResolve,
    resolvingIds,
    dialog: basisDialog,
  } = useResolveWithBasisCheck(businessId);
  const [hadItems, setHadItems] = useState(false);
  if (queueItems.length > 0 && !hadItems) setHadItems(true);
  // New filters keep the previous cards up until their own result arrives
  // (placeholderData), as the Invoices list does. A previous *empty* result
  // would announce "nothing matches" or "all caught up" before the request
  // has answered, so that one shows loading instead.
  const awaitingNewResult = isPlaceholderData && queueItems.length === 0;
  const loading = isPending || awaitingPosition || awaitingNewResult;
  const isCaughtUp =
    !loading && !isError && !filtered && queueItems.length === 0;
  // A new filter starts the list from the top, as on Invoices.
  const refilter =
    <T,>(set: (value: T) => unknown) =>
    (value: T) => {
      void set(value);
      if (viewportRef.current) viewportRef.current.scrollTop = 0;
    };

  const setViewport = useCallback(
    (node: HTMLDivElement | null) => {
      viewportRef.current = node;
      setNode(node);
    },
    [viewportRef, setNode],
  );

  return (
    <Stack align="start" gap={0} className="h-full min-h-0 w-full">
      {basisDialog}
      {/* Laid out like the Invoices filter row: the chips wrap on a phone,
          and search takes a line of its own. */}
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap={2}
        wrap
        className={cn("w-full flex-none pt-4", { hidden: isCaughtUp })}
      >
        <Stack
          direction="row"
          gap={2}
          align="center"
          wrap
          className="w-full sm:w-auto"
        >
          <OwnerAndActionFilters
            businessId={businessId}
            actionTypes={filters.actionTypes}
            onActionTypesChange={refilter(filters.setActionTypes)}
            assigneeTokens={filters.assigneeTokens}
            onAssigneeTokensChange={refilter(filters.setAssigneeTokens)}
          />
          <VendorFilter
            businessId={businessId}
            vendorIds={filters.vendorIds}
            onVendorIdsChange={refilter(filters.setVendorIds)}
          />
        </Stack>
        <Stack gap={0} align="stretch" className="w-full max-w-full sm:w-auto">
          <SearchInput
            placeholder="Vendor, invoice #, PO # or job #"
            value={filters.search}
            onChange={refilter(filters.setSearch)}
            className="max-w-full max-sm:mx-0 max-sm:w-full"
          />
        </Stack>
      </Stack>
      <Stack
        ref={setViewport}
        onScroll={onViewportScroll}
        className="min-h-0 w-full flex-1 overflow-y-auto"
        style={getEdgeFadeStyle(edges)}
      >
        {loading ? (
          <Stack align="start" gap={4} className="w-full pt-4">
            <InvoiceGroupCardSkeleton rows={3} />
            <InvoiceGroupCardSkeleton rows={3} />
            <InvoiceGroupCardSkeleton rows={2} />
          </Stack>
        ) : isError && (restoring || queueItems.length === 0) ? (
          <Stack
            gap={2}
            align="center"
            justify="center"
            className="min-h-0 w-full flex-1 px-6 py-8 text-center"
          >
            <span className="text-sm text-subtle">
              Couldn't load the invoices. Please try again.
            </span>
            <Button
              size="xs"
              variant="outline"
              disabled={isFetching}
              loading={isFetching}
              onClick={() => void queue.refetch()}
            >
              Try again
            </Button>
          </Stack>
        ) : queueItems.length > 0 ? (
          <Stack align="start" gap={4} className="w-full pt-4">
            {queueItems.map((item) => (
              <InvoiceGroupCard
                key={item.id}
                item={item}
                businessId={businessId}
                onResolve={onResolve}
                enableBulkApprove
                bulkApprovingIds={bulkApprovingIds}
                resolvingIds={resolvingIds}
                onOpenInvoice={openInvoice}
                actionTypes={filters.actionTypes}
                onApproveAll={onBulkApprove}
              />
            ))}
            {hasNextPage && (
              <Stack className="w-full" align="start" gap={4}>
                <LoadMoreButton
                  onClick={() => fetchNextPage()}
                  loading={isFetchingNextPage}
                  disabled={isFetching}
                />
              </Stack>
            )}
          </Stack>
        ) : (
          <ReviewQueueEmpty
            businessId={businessId}
            filtered={filtered}
            justCleared={hadItems}
            onClearFilters={filters.clear}
            onBrowseInvoices={onBrowseInvoices}
          />
        )}
      </Stack>
    </Stack>
  );
};
